import { Pagination } from "pepsico-ds";
import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { listHistoryLogs } from "../../../graphql/queries/listHistoryLogs";
import { getUpdatedCampaignConfiguration } from "../../campaign/campaignUtils";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../common/listingComponents/ListingComponentContext";
import ListingComponentWrapper from "../../common/listingComponents/ListingComponentWrapper";
import CampaignTable from "./CampaignTable";
import "./CampaignTable.scss";
import {
  campaignFilterJsonSchema,
  campaignFilterUiSchema,
  debounceFilterJsonSchema,
  debounceFilterUiSchema,
  transformCampaignFilterData,
} from "./mocks";

const CampaignTab = () => {
  const { campaignsConfiguration, campaignGroups } = useContext(GlobalConfigContext);

  const updatedCampaignConfiguration = useMemo(() => {
    return getUpdatedCampaignConfiguration(campaignsConfiguration, campaignGroups);
  }, [campaignsConfiguration, campaignGroups]);

  const campaignFilterSchema = useMemo(() => {
    return campaignFilterJsonSchema(updatedCampaignConfiguration);
  }, [updatedCampaignConfiguration]);

  return <CampaignTabWithPagination campaignFilterSchema={campaignFilterSchema} />;
};

const CampaignTabWithPagination = ({ campaignFilterSchema }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Calculate offset for current page
  const offset = (currentPage - 1) * pageSize;

  return (
    <ListingComponentProvider
      query={listHistoryLogs}
      fetchPolicy="cache-and-network"
      queryKey="listHistoryLogs"
      initialFilterData={{
        type: "campaign",
        showDiff: true,
      }}
      extraVariables={{
        offset: offset,
        limit: pageSize,
      }}
      noPagination={true}
      filterTransform={transformCampaignFilterData}
    >
      <CampaignListLog
        campaignFilterSchema={campaignFilterSchema}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
      />
    </ListingComponentProvider>
  );
};

const CampaignListLog = ({
  campaignFilterSchema,
  currentPage,
  setCurrentPage,
  pageSize,
}) => {
  const { setFilterData, viewableRecords } = useContext(ListingComponentContext);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
    }));
  }, [setFilterData]);

  // Get data from API response
  const filteredData = useMemo(() => {
    return viewableRecords?.items || [];
  }, [viewableRecords?.items]);

  // Calculate total pages from API response
  const totalPages = Math.ceil((viewableRecords?.total || 0) / pageSize);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-3 pt-3">
      <div className="container-card">
        <div className="page-content">
          <ListingComponentWrapper
            customStyles={{ flex: 1 }}
            filterClasses="filter-styles"
            offsetBasedPagination={false}
            filterConfig={{
              withDebounce: {
                jsonSchema: debounceFilterJsonSchema,
                uiSchema: debounceFilterUiSchema,
              },
              withoutDebounce: {
                jsonSchema: {
                  type: "object",
                  properties: {
                    ...campaignFilterSchema.properties,
                  },
                },
                uiSchema: {
                  type: "HorizontalLayout",
                  elements: [...campaignFilterUiSchema.elements],
                },
              },
            }}
            toggleViewType={false}
            showAllFiltersOption={true}
            enableAllFiltersButton={false}
          >
            <CampaignTable data={filteredData} source="campaigns" />
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                onUpdate={handlePageChange}
                pageCount={totalPages}
                size="medium"
                variant="number"
              />
            )}
          </ListingComponentWrapper>
        </div>
      </div>
    </div>
  );
};

export { CampaignListLog };
export default CampaignTab;
